import React, { useState, useEffect, useContext, useCallback } from "react";
import { useApolloClient } from "@apollo/client";
import queryString from "query-string";
import { useParams } from "react-router-dom";
import { SelectPagination } from "@foris/avocado-suite";
import { Subject } from "@models/ISchema";
import { IParams } from "@models/IParams";
import { Context } from "../../context/GroupsManagerContext";
import { Types } from "../../context/filters.reducer";
import { subjectSearch } from "../../graphql/subjectSearch.query";
import { SelectableOption } from "../../models";
import { useTranslation } from "react-i18next";

const SubjectSelector: React.FC = () => {
  const { t } = useTranslation();
  const { state, dispatch } = useContext(Context);
  const client = useApolloClient();
  const allSubjectsOption = {
    id: "*",
    value: "*",
    label: t("groups-manager.general-filters.search-by.key.course-selector.placeholder"),
    self: null,
  };
  const [selectedSubject, setSelectedSubject] = useState<SelectableOption<Subject>>(
    Boolean(state?.filters?.subject)
      ? {
          id: state?.filters?.subject?.id,
          value: state?.filters?.subject?.id,
          label: `${state?.filters?.subject?.code} | ${state?.filters?.subject?.name}`,
          self: state?.filters?.subject,
        }
      : allSubjectsOption,
  );
  const { origin, scenario }: IParams = useParams();
  const [, setSearchTerm] = useState("");
  const [prevSearchTerm, setPrevSearchTerm] = useState("");
  const [page, setPage] = useState(0);
  const params = queryString.parse(location.search);

  useEffect(() => {
    dispatch({ type: Types.SetSubject, payload: selectedSubject?.self });
    dispatch({ type: Types.SetCampus, payload: null });
  }, [selectedSubject]);

  /**
   * Clean the selection if the filters are updated from outside this component
   * (in particular, with the context's action `CleanFilters`).
   */
  useEffect(() => {
    if (!state?.filters?.subject) setSelectedSubject(allSubjectsOption);
  }, [state?.filters?.subject]);

  const requestItems = useCallback(
    async (searchTerm = "", page = 1) => {
      const size = 20;

      try {
        const variables = {
          query: subjectSearch,
          variables: {
            scenarioId: scenario,
            originId: origin,
            filterId: params?.advance,
            filter: {
              pagination: { page, size, searchTerm },
            },
          },
        };

        const { data } = await client.query(variables);
        const dataQuery = data?.data;
        const pageInfo = dataQuery?.groupsManagerSubjects?.pageInfo;
        const subjects = dataQuery?.groupsManagerSubjects?.items;

        const options = (subjects ?? []).map((subject: Subject) => ({
          id: subject?.id,
          value: subject?.id,
          label: `${subject?.code} | ${subject?.name}`,
          self: subject,
        }));

        return { pageInfo, options };
      } catch (error) {
        console.error(error);
        return {};
      }
    },
    [client, page],
  );

  const loadOptions = async (newSearchTerm: string) => {
    const newSearchPage = prevSearchTerm === newSearchTerm ? page + 1 : 1;

    setPrevSearchTerm(newSearchTerm);
    setPage(newSearchPage);

    const { pageInfo, options } = await requestItems(newSearchTerm, newSearchPage);

    return {
      options: newSearchPage === 1 ? [allSubjectsOption, ...(options ?? [])] : options,
      hasMore: pageInfo?.hasNextPage,
      additional: { page },
    };
  };

  return (
    <SelectPagination
      label={t("groups-manager.general-filters.search-by.key.course-selector.label")}
      value={selectedSubject}
      debounceTimeout={500}
      onInputChange={setSearchTerm}
      onChange={setSelectedSubject}
      loadOptions={loadOptions}
    />
  );
};

export default SubjectSelector;
